import React from 'react';
import { AcceptBlock, LangBlock, ShowNotify } from '../../global';
import { PRODUCTS_LIST, ProductID } from '../../../../shared/products';
import { SystemShared } from '../../../../shared/system';
import {
    AdminOrderCountOp,
    AdminOrderManageAction,
    AdminOrderManageFilter,
    AdminOrderManagePreview,
    AdminOrderManageRequest,
    AdminOrderPresenceKind,
    AdminOrderPreviewRow,
    AdminOrderProductScope,
    AdminOrderTarget,
    AdminOrderTargetStatus,
    AdminReferralActivity,
} from '../../../../shared/admin.orders';
import { langData } from '../../../../src/lang';
import { WebStorage } from '../../../storage';
import {
    AdminBadge,
    AdminButton,
    AdminFormField,
    AdminGrid,
    AdminPageWrap,
    AdminSection,
    AdminStatCard,
} from './admin.ui';
import { AdminDataTable } from './admin.table';
import { AdminPagination } from './admin.pagination';
import { usePaginatedTable } from './usePaginatedTable';

type FilterDraft = AdminOrderManageFilter & { id: number };

type AddFilterType =
    | AdminOrderPresenceKind
    | 'spent'
    | 'balance'
    | 'referral_count'
    | 'referral_income';

let filterSeq = 1;

const toDateInput = (ts: number) => {
    const date = new Date(ts * 1000);

    return `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, '0')}-${`${date.getDate()}`.padStart(
        2,
        '0',
    )}`;
};

const fromDateInput = (value: string) => Math.floor(new Date(`${value}T00:00:00`).getTime() / 1000);

const emptyScope = (): AdminOrderProductScope => ({ mode: 'any', products: [] });

const newFilter = (type: AddFilterType): FilterDraft => {
    const id = filterSeq++;

    if (type === 'spent' || type === 'balance') {
        return { id, type: 'range', kind: type, min: null, max: null };
    }

    if (type === 'referral_count' || type === 'referral_income') {
        return {
            id,
            type: 'referral',
            kind: type === 'referral_count' ? 'count' : 'income',
            activity: 'all',
            products: emptyScope(),
            min: null,
            max: null,
        };
    }

    return {
        id,
        type: 'presence',
        kind: type,
        products: emptyScope(),
        count: { op: 'any' },
    };
};

const defaultAction = (): AdminOrderManageAction => ({
    type: 'extend',
    days: 30,
    fromTodayIfExpired: true,
    addRemainder: false,
});

const defaultTarget = (): AdminOrderTarget => ({
    products: [],
    status: 'any',
    expiredAfter: null,
});

const ADD_FILTER_OPTIONS: { value: AddFilterType; label: langData }[] = [
    { value: 'has_active', label: 'AdminFilterHasActive' },
    { value: 'no_active', label: 'AdminFilterNoActive' },
    { value: 'has_inactive', label: 'AdminFilterHasInactive' },
    { value: 'no_inactive', label: 'AdminFilterNoInactive' },
    { value: 'spent', label: 'AdminFilterSpent' },
    { value: 'balance', label: 'AdminFilterBalance' },
    { value: 'referral_count', label: 'AdminFilterRefCount' },
    { value: 'referral_income', label: 'AdminFilterRefIncome' },
];

const COUNT_OPS: { value: AdminOrderCountOp; label: langData }[] = [
    { value: 'any', label: 'AdminCountOpAny' },
    { value: 'eq', label: 'AdminCountOpEq' },
    { value: 'gte', label: 'AdminCountOpGte' },
    { value: 'lte', label: 'AdminCountOpLte' },
    { value: 'range', label: 'AdminCountOpRange' },
];

const ACTIVITY_OPTIONS: { value: AdminReferralActivity; label: langData }[] = [
    { value: 'all', label: 'AdminActivityAll' },
    { value: 'has_active', label: 'AdminActivityHasActive' },
    { value: 'no_active', label: 'AdminActivityNoActive' },
    { value: 'has_inactive', label: 'AdminActivityHasInactive' },
    { value: 'no_inactive', label: 'AdminActivityNoInactive' },
];

const STATUS_OPTIONS: { value: AdminOrderTargetStatus; label: langData }[] = [
    { value: 'any', label: 'AdminStatusAny' },
    { value: 'active', label: 'AdminStatusActive' },
    { value: 'inactive', label: 'AdminStatusInactive' },
];

const CHANGE_LABELS: Record<AdminOrderPreviewRow['change'], langData> = {
    create: 'AdminChangeCreate',
    replace: 'AdminChangeReplace',
    extend: 'AdminChangeExtend',
    skip: 'AdminChangeSkip',
};

const FILTER_TITLE: Record<string, langData> = {
    has_active: 'AdminFilterHasActive',
    no_active: 'AdminFilterNoActive',
    has_inactive: 'AdminFilterHasInactive',
    no_inactive: 'AdminFilterNoInactive',
    spent: 'AdminFilterSpent',
    balance: 'AdminFilterBalance',
    count: 'AdminFilterRefCount',
    income: 'AdminFilterRefIncome',
};

const NativeSelect = <T extends string | number>({
    value,
    onChange,
    options,
}: {
    value: T;
    onChange: (value: T) => void;
    options: { value: T; label: string }[];
}) => (
    <select
        className="admin-input"
        value={String(value)}
        onChange={(event) => {
            const next = options.find((item) => String(item.value) === event.target.value);
            if (next) {
                onChange(next.value);
            }
        }}
    >
        {options.map((item) => (
            <option key={String(item.value)} value={String(item.value)}>
                {item.label}
            </option>
        ))}
    </select>
);

const NumberInput = ({
    value,
    onChange,
    placeholder,
    min,
    step = 1,
}: {
    value: number | null | undefined;
    onChange: (value: number | null) => void;
    placeholder?: string;
    min?: number;
    step?: number;
}) => (
    <input
        className="admin-input"
        type="number"
        min={min}
        step={step}
        placeholder={placeholder}
        value={value == null || Number.isNaN(value) ? '' : value}
        onChange={(event) => {
            const raw = event.target.value;
            onChange(raw === '' ? null : Number(raw));
        }}
    />
);

const CheckLine = ({
    checked,
    onChange,
    children,
}: {
    checked: boolean;
    onChange: (value: boolean) => void;
    children: React.ReactNode;
}) => (
    <label className="admin-check">
        <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
        <span>{children}</span>
    </label>
);

const ProductChips = ({
    selected,
    onChange,
    productLabel,
}: {
    selected: ProductID[];
    onChange: (value: ProductID[]) => void;
    productLabel: (id: ProductID) => string;
}) => (
    <div className="admin-chips">
        {PRODUCTS_LIST.map((product) => {
            const on = selected.includes(product.type);

            return (
                <button
                    key={product.type}
                    type="button"
                    className={`admin-chip${on ? ' admin-chip--on' : ''}`}
                    onClick={() =>
                        onChange(
                            on
                                ? selected.filter((item) => item !== product.type)
                                : [...selected, product.type],
                        )
                    }
                >
                    {productLabel(product.type)}
                </button>
            );
        })}
    </div>
);

const ProductScopeFields = ({
    value,
    onChange,
    productLabel,
}: {
    value: AdminOrderProductScope;
    onChange: (value: AdminOrderProductScope) => void;
    productLabel: (id: ProductID) => string;
}) => {
    const [, , LangString] = LangBlock();

    return (
    <>
        <AdminFormField label={LangString('AdminProducts')}>
            <NativeSelect
                value={value.mode}
                onChange={(mode) => onChange({ ...value, mode })}
                options={[
                    { value: 'any', label: LangString('AdminProductAny') },
                    { value: 'specific', label: LangString('AdminProductSpecific') },
                ]}
            />
        </AdminFormField>
        {value.mode === 'specific' ? (
            <AdminFormField label={LangString('AdminSelectProducts')} wide>
                <ProductChips
                    selected={value.products}
                    onChange={(products) => onChange({ ...value, products })}
                    productLabel={productLabel}
                />
            </AdminFormField>
        ) : null}
    </>
    );
};

const RangeFields = ({
    min,
    max,
    onChange,
}: {
    min?: number | null;
    max?: number | null;
    onChange: (min: number | null, max: number | null) => void;
    prefix?: string;
}) => {
    const [, , LangString] = LangBlock();

    return (
    <>
        <AdminFormField label={LangString('AdminFrom')}>
            <NumberInput value={min} onChange={(value) => onChange(value, max ?? null)} placeholder={LangString('AdminNoMin')} step={0.01} />
        </AdminFormField>
        <AdminFormField label={LangString('AdminTo')}>
            <NumberInput value={max} onChange={(value) => onChange(min ?? null, value)} placeholder={LangString('AdminNoMax')} step={0.01} />
        </AdminFormField>
    </>
    );
};

const presenceTitle = (kind: AdminOrderPresenceKind, translate: (id: langData) => string) =>
    translate(FILTER_TITLE[kind] || 'AdminFilterNoInactive');

const formatEnd = (end: number | undefined, lifetimeLabel: string) => {
    if (end == null) {
        return '—';
    }

    if (end === -1) {
        return lifetimeLabel;
    }

    return SystemShared.timeStampString(end, true);
};

const changeTone = (change: AdminOrderPreviewRow['change']) => {
    if (change === 'create') {
        return 'ok' as const;
    }

    if (change === 'replace') {
        return 'warn' as const;
    }

    if (change === 'extend') {
        return 'neutral' as const;
    }

    return 'danger' as const;
};

export const AdminOrderManage = () => {
    const [, , LangString] = LangBlock();
    const productLabel = React.useCallback(
        (id: ProductID) =>
            LangString(
                (PRODUCTS_LIST.find((item) => item.type === id)?.name || 'AdminProduct') as langData,
            ),
        [LangString],
    );
    const withLang = <T extends string | number>(options: { value: T; label: langData }[]) =>
        options.map((item) => ({ value: item.value, label: LangString(item.label) }));
    const lifetimeLabel = LangString('AdminLifetime');

    const [filters, setFilters] = React.useState<FilterDraft[]>([]);
    const [addType, setAddType] = React.useState<AddFilterType>('has_active');
    const [target, setTarget] = React.useState<AdminOrderTarget>(defaultTarget);
    const [action, setAction] = React.useState<AdminOrderManageAction>(defaultAction);
    const [lifetime, setLifetime] = React.useState(false);
    const [preview, setPreview] = React.useState<AdminOrderManagePreview | null>(null);
    const [previewKey, setPreviewKey] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [applying, setApplying] = React.useState(false);

    const request: AdminOrderManageRequest = React.useMemo(() => {
        const nextAction: AdminOrderManageAction =
            action.type === 'issue'
                ? { ...action, days: lifetime ? -1 : action.days }
                : action;

        return {
            filters: filters.map(({ id: _id, ...filter }) => filter),
            target,
            action: nextAction,
        };
    }, [filters, target, action, lifetime]);

    const requestKey = React.useMemo(() => JSON.stringify(request), [request]);
    const stale = !!preview && previewKey !== requestKey;
    const table = usePaginatedTable(preview?.rows || [], 25);

    const updateFilter = (id: number, next: AdminOrderManageFilter) => {
        setFilters((list) => list.map((item) => (item.id === id ? { ...next, id } : item)));
    };

    const send = async (path: '/api/admin/orders/manage/preview' | '/api/admin/orders/manage/execute') => {
        return WebStorage.ApiRequestPost(path, {
            payload: JSON.stringify(request),
        });
    };

    const loadPreview = async () => {
        setLoading(true);

        const res = await send('/api/admin/orders/manage/preview');

        setLoading(false);

        if (!res.status) {
            ShowNotify(LangString('AdminNotifyTitle'), res.error_message || LangString('AdminPreviewFailed'), 'error');
            return;
        }

        setPreview(res as AdminOrderManagePreview);
        setPreviewKey(requestKey);
        table.setPage(1);
    };

    const execute = async () => {
        if (!preview || stale) {
            ShowNotify(LangString('AdminNotifyTitle'), LangString('AdminLoadPreviewFirst'), 'error');
            return;
        }

        const impact =
            request.action.type === 'issue'
                ? LangString('AdminImpactIssue', preview.created, preview.clients)
                : request.action.type === 'replace'
                  ? LangString('AdminImpactReplace', preview.replaced, preview.clients)
                  : LangString('AdminImpactExtend', preview.extended, preview.clients);

        if (!(await AcceptBlock(LangString('AdminApplyConfirm', impact)))) {
            return;
        }

        setApplying(true);

        const res = await send('/api/admin/orders/manage/execute');

        setApplying(false);

        if (!res.status) {
            ShowNotify(LangString('AdminNotifyTitle'), res.error_message || LangString('AdminApplyFailed'), 'error');
            return;
        }

        ShowNotify(LangString('AdminNotifyTitle'), LangString('AdminActionApplied'), 'success');
        setPreview(res as AdminOrderManagePreview);
        setPreviewKey(requestKey);
    };

    return (
        <AdminPageWrap>
            <AdminSection
                title={LangString('AdminOrderManageTitle')}
                subtitle={LangString('AdminOrderManageSubtitle')}
            >
                <div className="admin-manage-toolbar">
                    <NativeSelect
                        value={addType}
                        onChange={(value) => setAddType(value)}
                        options={ADD_FILTER_OPTIONS.map((item) => ({
                            value: item.value,
                            label: LangString(item.label),
                        }))}
                    />
                    <AdminButton
                        variant="ghost"
                        onClick={() => setFilters((list) => [...list, newFilter(addType)])}
                    >
                        {LangString('AdminAddFilter')}
                    </AdminButton>
                    {filters.length ? (
                        <AdminButton variant="ghost" onClick={() => setFilters([])}>
                            {LangString('AdminClearFilters')}
                        </AdminButton>
                    ) : null}
                </div>

                {!filters.length ? (
                    <p className="admin-manage-hint">
                        {LangString('AdminNoFiltersHint')}
                    </p>
                ) : null}

                <p className="admin-manage-hint">
                    {LangString('AdminMigrationHint')}
                </p>

                {filters.map((filter, index) => (
                    <React.Fragment key={filter.id}>
                        {index > 0 ? <div className="admin-and">{LangString('AdminAnd')}</div> : null}
                        <div className="admin-filter-card">
                            <div className="admin-filter-card__head">
                                <strong>
                                    {filter.type === 'presence'
                                        ? presenceTitle(filter.kind, LangString)
                                        : filter.type === 'range'
                                          ? LangString(FILTER_TITLE[filter.kind])
                                          : LangString(FILTER_TITLE[filter.kind])}
                                </strong>
                                <AdminButton
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setFilters((list) => list.filter((item) => item.id !== filter.id))}
                                >
                                    {LangString('AdminRemove')}
                                </AdminButton>
                            </div>
                            <div className="admin-form-grid">
                                {filter.type === 'presence' ? (
                                    <>
                                        <ProductScopeFields
                                            value={filter.products}
                                            onChange={(products) => updateFilter(filter.id, { ...filter, products })}
                                            productLabel={productLabel}
                                        />
                                        <AdminFormField label={LangString('AdminCount')}>
                                            <NativeSelect
                                                value={filter.count.op}
                                                onChange={(op) =>
                                                    updateFilter(filter.id, {
                                                        ...filter,
                                                        count: { ...filter.count, op },
                                                    })
                                                }
                                                options={withLang(COUNT_OPS)}
                                            />
                                        </AdminFormField>
                                        {filter.count.op === 'eq' ||
                                        filter.count.op === 'gte' ||
                                        filter.count.op === 'lte' ? (
                                            <AdminFormField label={LangString('AdminValue')}>
                                                <NumberInput
                                                    value={filter.count.value ?? null}
                                                    min={0}
                                                    onChange={(value) =>
                                                        updateFilter(filter.id, {
                                                            ...filter,
                                                            count: { ...filter.count, value: value ?? 0 },
                                                        })
                                                    }
                                                />
                                            </AdminFormField>
                                        ) : null}
                                        {filter.count.op === 'range' ? (
                                            <RangeFields
                                                min={filter.count.min}
                                                max={filter.count.max}
                                                onChange={(min, max) =>
                                                    updateFilter(filter.id, {
                                                        ...filter,
                                                        count: { ...filter.count, min: min ?? undefined, max: max ?? undefined },
                                                    })
                                                }
                                            />
                                        ) : null}
                                        {filter.count.op === 'any' ? (
                                            <AdminFormField label={LangString('AdminMeaning')} wide>
                                                <p className="admin-manage-hint">
                                                    {filter.kind.startsWith('no_')
                                                        ? LangString('AdminCountDefaultZero')
                                                        : LangString('AdminCountDefaultOne')}
                                                </p>
                                            </AdminFormField>
                                        ) : null}
                                    </>
                                ) : null}

                                {filter.type === 'range' ? (
                                    <RangeFields
                                        min={filter.min}
                                        max={filter.max}
                                        onChange={(min, max) => updateFilter(filter.id, { ...filter, min, max })}
                                    />
                                ) : null}

                                {filter.type === 'referral' ? (
                                    <>
                                        <AdminFormField label={LangString('AdminReferralActivity')}>
                                            <NativeSelect
                                                value={filter.activity}
                                                onChange={(activity) =>
                                                    updateFilter(filter.id, { ...filter, activity })
                                                }
                                                options={withLang(ACTIVITY_OPTIONS)}
                                            />
                                        </AdminFormField>
                                        <ProductScopeFields
                                            value={filter.products}
                                            onChange={(products) => updateFilter(filter.id, { ...filter, products })}
                                            productLabel={productLabel}
                                        />
                                        <RangeFields
                                            min={filter.min}
                                            max={filter.max}
                                            onChange={(min, max) =>
                                                updateFilter(filter.id, { ...filter, min, max })
                                            }
                                        />
                                    </>
                                ) : null}
                            </div>
                        </div>
                    </React.Fragment>
                ))}
            </AdminSection>

            <AdminSection
                title={LangString('AdminTargetOrders')}
                subtitle={LangString('AdminTargetOrdersSubtitle')}
            >
                <div className="admin-form-grid">
                    <AdminFormField label={LangString('AdminStatus')}>
                        <NativeSelect
                            value={target.status}
                            onChange={(status) => setTarget((old) => ({ ...old, status }))}
                            options={withLang(STATUS_OPTIONS)}
                        />
                    </AdminFormField>
                    <AdminFormField label={LangString('AdminExpiredOnOrAfter')}>
                        <input
                            className="admin-input"
                            type="date"
                            value={target.expiredAfter ? toDateInput(target.expiredAfter) : ''}
                            onChange={(event) =>
                                setTarget((old) => ({
                                    ...old,
                                    expiredAfter: event.target.value ? fromDateInput(event.target.value) : null,
                                }))
                            }
                        />
                    </AdminFormField>
                    <AdminFormField label={LangString('AdminProductsEmptyAll')} wide>
                        <ProductChips
                            selected={target.products}
                            onChange={(products) => setTarget((old) => ({ ...old, products }))}
                            productLabel={productLabel}
                        />
                    </AdminFormField>
                </div>
            </AdminSection>

            <AdminSection title={LangString('AdminAction')} subtitle={LangString('AdminActionSubtitle')}>
                <div className="admin-action-cards">
                    {(
                        [
                            ['issue', 'AdminActionIssue'],
                            ['replace', 'AdminActionReplace'],
                            ['extend', 'AdminActionExtend'],
                        ] as const
                    ).map(([type, label]) => (
                        <button
                            key={type}
                            type="button"
                            className={`admin-action-card${action.type === type ? ' admin-action-card--on' : ''}`}
                            onClick={() => {
                                if (type === 'issue') {
                                    setAction({ type: 'issue', product: 'application_plus', days: 30 });
                                    setLifetime(false);
                                } else if (type === 'replace') {
                                    setAction({
                                        type: 'replace',
                                        from: 'application',
                                        to: 'application_plus',
                                    });
                                } else {
                                    setAction(defaultAction());
                                }
                            }}
                        >
                            {LangString(label)}
                        </button>
                    ))}
                </div>

                <div className="admin-form-grid">
                    {action.type === 'issue' ? (
                        <>
                            <AdminFormField label={LangString('AdminProduct')}>
                                <NativeSelect
                                    value={action.product}
                                    onChange={(product) => setAction({ ...action, product })}
                                    options={PRODUCTS_LIST.map((item) => ({
                                        value: item.type,
                                        label: productLabel(item.type),
                                    }))}
                                />
                            </AdminFormField>
                            <AdminFormField label={LangString('AdminDays')}>
                                <NumberInput
                                    value={lifetime ? -1 : action.days}
                                    min={1}
                                    onChange={(value) =>
                                        setAction({ ...action, days: value && value > 0 ? value : 1 })
                                    }
                                />
                            </AdminFormField>
                            <AdminFormField label={LangString('AdminLifetime')}>
                                <CheckLine
                                    checked={lifetime}
                                    onChange={(value) => {
                                        setLifetime(value);
                                    }}
                                >
                                    {LangString('AdminIssueLifetime')}
                                </CheckLine>
                            </AdminFormField>
                        </>
                    ) : null}

                    {action.type === 'replace' ? (
                        <>
                            <AdminFormField label={LangString('AdminFrom')}>
                                <NativeSelect
                                    value={action.from}
                                    onChange={(from) => setAction({ ...action, from })}
                                    options={PRODUCTS_LIST.map((item) => ({
                                        value: item.type,
                                        label: productLabel(item.type),
                                    }))}
                                />
                            </AdminFormField>
                            <AdminFormField label={LangString('AdminTo')}>
                                <NativeSelect
                                    value={action.to}
                                    onChange={(to) => setAction({ ...action, to })}
                                    options={PRODUCTS_LIST.map((item) => ({
                                        value: item.type,
                                        label: productLabel(item.type),
                                    }))}
                                />
                            </AdminFormField>
                        </>
                    ) : null}

                    {action.type === 'extend' ? (
                        <>
                            <AdminFormField label={LangString('AdminDays')}>
                                <NumberInput
                                    value={action.days}
                                    min={1}
                                    onChange={(value) =>
                                        setAction({ ...action, days: value && value > 0 ? value : 1 })
                                    }
                                />
                            </AdminFormField>
                            <AdminFormField label={LangString('AdminExpiredOrders')} wide>
                                <CheckLine
                                    checked={action.fromTodayIfExpired}
                                    onChange={(fromTodayIfExpired) =>
                                        setAction({ ...action, fromTodayIfExpired })
                                    }
                                >
                                    {LangString('AdminExtendFromToday')}
                                </CheckLine>
                                <CheckLine
                                    checked={action.addRemainder}
                                    onChange={(addRemainder) => setAction({ ...action, addRemainder })}
                                >
                                    {LangString('AdminExtendAddRemainder')}
                                </CheckLine>
                            </AdminFormField>
                        </>
                    ) : null}
                </div>

                {action.type === 'extend' && action.addRemainder && !target.expiredAfter ? (
                    <p className="admin-manage-hint">
                        {LangString('AdminExtendNeedDate')}
                    </p>
                ) : null}

                <div className="admin-manage-toolbar">
                    <AdminButton variant="primary" disabled={loading} onClick={loadPreview}>
                        {loading ? LangString('AdminLoadingPreview') : LangString('AdminLoadPreview')}
                    </AdminButton>
                    <AdminButton
                        variant="danger"
                        disabled={applying || !preview || stale || preview.totalRows === 0}
                        onClick={execute}
                    >
                        {applying ? LangString('AdminApplying') : LangString('AdminApplyAction')}
                    </AdminButton>
                </div>
            </AdminSection>

            <AdminSection
                title={LangString('AdminImpactPreview')}
                subtitle={
                    stale
                        ? LangString('AdminImpactStale')
                        : preview
                          ? LangString('AdminImpactReady')
                          : LangString('AdminImpactEmpty')
                }
            >
                {preview ? (
                    <>
                        <AdminGrid columns={4}>
                            <AdminStatCard label={LangString('AdminClients')} value={preview.clients} />
                            <AdminStatCard
                                label={
                                    action.type === 'issue'
                                        ? LangString('AdminNewOrders')
                                        : LangString('AdminOrders')
                                }
                                value={action.type === 'issue' ? preview.created : preview.orders}
                                tone="ok"
                            />
                            <AdminStatCard
                                label={LangString('AdminSkipped')}
                                value={preview.skipped}
                                tone={preview.skipped ? 'warn' : 'neutral'}
                            />
                            <AdminStatCard
                                label={LangString('AdminRemainderAdded')}
                                value={
                                    preview.remainderSeconds
                                        ? SystemShared.secondsToString(preview.remainderSeconds)
                                        : '—'
                                }
                            />
                        </AdminGrid>

                        {preview.clients > 100 ? (
                            <p className="admin-manage-hint">
                                {LangString('AdminAffectsMany')}
                            </p>
                        ) : null}

                        {preview.truncated ? (
                            <p className="admin-manage-hint">
                                {LangString('AdminShowingRows', preview.rows.length, preview.totalRows)}
                            </p>
                        ) : null}

                        <AdminDataTable
                            data={table.pageData}
                            sortColumn={table.sortColumn}
                            sortType={table.sortType}
                            onSort={table.handleSort}
                            rowKey={(row, index) => `${row.userId}-${row.orderId || 'new'}-${index}`}
                            emptyMessage={LangString('AdminNothingMatches')}
                            columns={[
                                {
                                    key: 'userName',
                                    label: LangString('AdminClient'),
                                    sortable: true,
                                    primary: true,
                                    render: (row) => `${row.userName} (#${row.userId})`,
                                },
                                {
                                    key: 'orderId',
                                    label: LangString('AdminOrder'),
                                    sortable: true,
                                    render: (row) =>
                                        row.orderId ? `#${row.orderId}` : LangString('AdminOrderNew'),
                                },
                                {
                                    key: 'product',
                                    label: LangString('AdminProduct'),
                                    render: (row) =>
                                        row.proposedProduct && row.product && row.proposedProduct !== row.product
                                            ? `${productLabel(row.product)} → ${productLabel(row.proposedProduct)}`
                                            : productLabel((row.proposedProduct || row.product) as ProductID),
                                },
                                {
                                    key: 'end',
                                    label: LangString('AdminCurrentEnd'),
                                    render: (row) => formatEnd(row.end, lifetimeLabel),
                                },
                                {
                                    key: 'proposedEnd',
                                    label: LangString('AdminProposedEnd'),
                                    render: (row) => formatEnd(row.proposedEnd, lifetimeLabel),
                                },
                                {
                                    key: 'remainderSeconds',
                                    label: LangString('AdminRemainder'),
                                    render: (row) =>
                                        row.remainderSeconds
                                            ? SystemShared.secondsToString(row.remainderSeconds)
                                            : '—',
                                },
                                {
                                    key: 'change',
                                    label: LangString('AdminChange'),
                                    render: (row) => {
                                        const changeLabel = LangString(CHANGE_LABELS[row.change]);
                                        const noteLabel =
                                            row.note === 'lifetime'
                                                ? LangString('AdminChangeLifetime')
                                                : row.note;

                                        return (
                                            <AdminBadge tone={changeTone(row.change)}>
                                                {noteLabel ? `${changeLabel} (${noteLabel})` : changeLabel}
                                            </AdminBadge>
                                        );
                                    },
                                },
                            ]}
                        />
                        <AdminPagination
                            page={table.page}
                            totalPages={table.totalPages}
                            total={table.total}
                            limit={table.limit}
                            limitOptions={[10, 25, 50, 100]}
                            onPageChange={table.setPage}
                            onLimitChange={table.setLimit}
                        />
                    </>
                ) : (
                    <p className="admin-manage-hint">{LangString('AdminRunPreview')}</p>
                )}
            </AdminSection>
        </AdminPageWrap>
    );
};
